import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/navigation';
import { apiGetBanners } from '../../api';
import { useToast } from '../../context/ToastContext';
import { resolveBannerLink } from '../../utils/bannerLink';
import { getAssetImage, handleAssetImageError } from '../../utils/image';
import { updateBannerHeight } from '../../utils/carousel';

const StoreSection = () => {
  const { addToast } = useToast();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    apiGetBanners('store')
      .then((data) => setBanners(data.banners || []))
      .catch((error) => {
        setBanners([]);
        addToast(error.message || 'Failed to load store banners', 'error');
      })
      .finally(() => setLoading(false));
  }, [addToast]);

  if (loading || banners.length === 0) return null;

  return (
    <section className="carousel-section banner-carousel w-full px-3 py-3 sm:px-4" aria-label="Ekamithila Stores">
      <div className="carousel-controls hidden items-center justify-end mb-3 md:flex">
        <div className="flex items-center gap-2">
          <button
            ref={prevRef}
            className="w-9 h-9 rounded-full border border-gray-400 flex items-center justify-center hover:bg-gray-100 transition"
            aria-label="Previous"
          >
            <FiChevronLeft className="text-lg text-black" />
          </button>
          <button
            ref={nextRef}
            className="w-9 h-9 rounded-full border border-gray-400 flex items-center justify-center hover:bg-gray-100 transition"
            aria-label="Next"
          >
            <FiChevronRight className="text-lg text-black" />
          </button>
        </div>
      </div>
      <Swiper
        autoHeight
        watchOverflow
        grabCursor={banners.length > 1}
        modules={[Navigation, Autoplay]}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        autoplay={banners.length > 1 ? { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
        loop={banners.length > 1}
        slidesPerView={1}
        className="store-swiper"
      >
        {banners.map((banner) => {
          const link = resolveBannerLink(banner.link);
          const img = (
            <img
              src={getAssetImage(banner.image)}
              onError={handleAssetImageError}
              onLoad={updateBannerHeight}
              alt={banner.title || 'Store banner'}
              className="block w-full h-auto object-contain rounded-xl"
              loading="lazy"
            />
          );
          return (
            <SwiperSlide key={banner.id}>
              {link ? (
                link.internal ? (
                  <Link className="block rounded-xl overflow-hidden" to={link.to}>{img}</Link>
                ) : (
                  <a className="block rounded-xl overflow-hidden" href={link.href} target="_blank" rel="noreferrer">{img}</a>
                )
              ) : (
                img
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

export default StoreSection;
